<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import RosterDetailsModal from '../RosterDetailsModal.vue';
import TournamentRosterCard from './TournamentRosterCard.vue';
import Avatar from '../../shared/Avatar.vue';

interface TournamentPlayer {
  id: number;
  name: string;
  avatar_url?: string;
  faction_name?: string;
}

interface TournamentRoster {
  id: number;
  hero_name?: string;
  faction_name: string;
  structure?: any;
  tournament_roster_url?: string;
}

interface Props {
  player: TournamentPlayer;
  victoryPoints: number;
  modelsCost: number;
  rosters?: TournamentRoster[];
  activeRosterIndex?: number;
  matchId?: number;
  eventId?: number;
  isWinner?: boolean;
  isLoser?: boolean;
  isTie?: boolean;
  isCompleted?: boolean;
  isDraggable?: boolean;
  isDropZone?: boolean;
  isCurrentOpponent?: boolean;
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isWinner: false,
  isLoser: false,
  isTie: false,
  isCompleted: false,
  isDraggable: true,
  isDropZone: false,
  isCurrentOpponent: false,
  compact: false
});

const { t } = useI18n();

const emit = defineEmits<{
  'update:victoryPoints': [value: number];
  'update:modelsCost': [value: number];
  'dragstart': [player: TournamentPlayer];
  'drop': [player: TournamentPlayer];
}>();

const isDragging = ref(false);
const isDragOver = ref(false);
const victoryPointsValid = ref(true);
const modelsCostValid = ref(true);
const showRosterModal = ref(false);
const selectedRoster = ref<TournamentRoster | null>(null);
const rosterUpdateSuccess = ref(false);

const isDummyPlayer = computed(() => props.player.name === 'Dummy Player');

// Computed properties for roster handling
const activeRoster = computed(() => {
  if (!props.rosters || props.rosters.length === 0) return null;
  const index = props.activeRosterIndex || 0;
  return props.rosters[index] || props.rosters[0];
});

const hasMultipleRosters = computed(() => {
  return props.rosters && props.rosters.length > 1;
});

const cardClasses = computed(() => ({
  'player-card': true,
  'winner': props.isWinner,
  'loser': props.isLoser,
  'tie': props.isTie,
  'completed': props.isCompleted,
  'draggable': props.isDraggable && !props.isCompleted && !isDummyPlayer.value,
  'drop-zone': props.isDropZone && !props.isCurrentOpponent && !isDummyPlayer.value,
  'drag-over': isDragOver.value,
  'dragging': isDragging.value,
  'current-opponent': props.isCurrentOpponent,
  'dummy-player': isDummyPlayer.value,
  'compact': props.compact
}));

const handleDragStart = (event: DragEvent) => {
  if (!props.isDraggable || props.isCompleted) return;

  isDragging.value = true;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', props.player.id.toString());
  }
  emit('dragstart', props.player);
};

const handleDragEnd = () => {
  isDragging.value = false;
};

const handleDragOver = (event: DragEvent) => {
  if (!props.isDropZone || props.isCurrentOpponent) return;

  event.preventDefault();
  event.dataTransfer!.dropEffect = 'move';
  isDragOver.value = true;
};

const handleDragLeave = () => {
  isDragOver.value = false;
};

const handleDrop = (event: DragEvent) => {
  if (!props.isDropZone || props.isCurrentOpponent) return;

  event.preventDefault();
  isDragOver.value = false;
  emit('drop', props.player);
};

const handleVictoryPointsChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  // Remove any non-digit characters except for the minus sign at the beginning
  value = value.replace(/[^\d-]/g, '');

  // Ensure only one minus sign at the beginning
  if (value.startsWith('-')) {
    value = '-' + value.substring(1).replace(/-/g, '');
  }

  // Parse the value
  let numValue = parseInt(value) || 0;

  // Ensure the value is non-negative
  if (numValue < 0) {
    numValue = 0;
  }

  // Validate the value
  victoryPointsValid.value = numValue >= 0 && Number.isInteger(numValue);

  // Update the input field with the validated value
  target.value = numValue.toString();

  emit('update:victoryPoints', numValue);
};

const handleModelsCostChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  // Remove any non-digit characters except for the minus sign at the beginning
  value = value.replace(/[^\d-]/g, '');

  // Ensure only one minus sign at the beginning
  if (value.startsWith('-')) {
    value = '-' + value.substring(1).replace(/-/g, '');
  }

  // Parse the value
  let numValue = parseInt(value) || 0;

  // Ensure the value is non-negative
  if (numValue < 0) {
    numValue = 0;
  }

  // Validate the value
  modelsCostValid.value = numValue >= 0 && Number.isInteger(numValue);

  // Update the input field with the validated value
  target.value = numValue.toString();

  emit('update:modelsCost', numValue);
};

// Add validation for keydown events to prevent invalid characters
const handleKeyDown = (event: KeyboardEvent) => {
  // Allow: backspace, delete, tab, escape, enter, and navigation keys
  if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(event.keyCode)) {
    return;
  }

  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
  if (event.ctrlKey && [65, 67, 86, 88].includes(event.keyCode)) {
    return;
  }

  // Allow: numbers and minus sign
  if ((event.keyCode >= 48 && event.keyCode <= 57) || event.keyCode === 45) {
    // If minus sign, only allow it at the beginning
    if (event.keyCode === 45) {
      const target = event.target as HTMLInputElement;
      if (target.selectionStart !== 0) {
        event.preventDefault();
        return;
      }
    }
    return;
  }

  // Prevent all other keys
  event.preventDefault();
};

// Add blur validation
const handleBlur = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value) || 0;

  if (target.classList.contains('victory-points-input')) {
    victoryPointsValid.value = value >= 0 && Number.isInteger(value);
    if (value < 0) {
      target.value = '0';
      emit('update:victoryPoints', 0);
    }
  } else if (target.classList.contains('models-cost-input')) {
    modelsCostValid.value = value >= 0 && Number.isInteger(value);
    if (value < 0) {
      target.value = '0';
      emit('update:modelsCost', 0);
    }
  }
};

// Add focus handler to clear validation errors
const handleFocus = (event: Event) => {
  const target = event.target as HTMLInputElement;

  if (target.classList.contains('victory-points-input')) {
    victoryPointsValid.value = true;
  } else if (target.classList.contains('models-cost-input')) {
    modelsCostValid.value = true;
  }
};

// Add paste validation
const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedText = event.clipboardData?.getData('text/plain') || '';

  // Only allow digits and minus sign
  const cleanText = pastedText.replace(/[^\d-]/g, '');

  if (cleanText) {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value;
    const selectionStart = target.selectionStart || 0;
    const selectionEnd = target.selectionEnd || 0;

    // Insert the cleaned text at cursor position
    const newValue = currentValue.substring(0, selectionStart) + cleanText + currentValue.substring(selectionEnd);

    // Validate the new value
    let numValue = parseInt(newValue) || 0;
    if (numValue < 0) {
      numValue = 0;
    }

    target.value = numValue.toString();

    // Trigger the appropriate update
    if (target.classList.contains('victory-points-input')) {
      emit('update:victoryPoints', numValue);
    } else if (target.classList.contains('models-cost-input')) {
      emit('update:modelsCost', numValue);
    }
  }
};

const showRosterDetails = (roster: TournamentRoster) => {
  selectedRoster.value = roster;
  showRosterModal.value = true;
};

const openRosterLink = (roster: TournamentRoster) => {
  // This will be handled by the link in the component
};

const closeRosterModal = () => {
  showRosterModal.value = false;
  selectedRoster.value = null;
};

const handleRosterSelection = async (rosterIndex: number) => {
  // Prevent roster switching for completed matches
  if (props.isCompleted) return;

  if (!props.matchId || !props.eventId) return;

  try {
    await axios.post(`/api/v1/events/tournament/update_active_roster`, {
      event_id: props.eventId,
      match_id: props.matchId,
      player_id: props.player.id,
      roster_index: rosterIndex
    });

    // Emit an event with specific match data for targeted update
    window.dispatchEvent(new CustomEvent('tournament-roster-updated', {
      detail: {
        matchId: props.matchId,
        playerId: props.player.id,
        rosterIndex: rosterIndex
      }
    }));

    // Show success feedback
    rosterUpdateSuccess.value = true;
    setTimeout(() => {
      rosterUpdateSuccess.value = false;
    }, 1000);
  } catch (error) {
    console.error('Error updating active roster:', error);
  }
};
</script>

<template>
  <div
    :class="cardClasses"
    :draggable="isDraggable && !isCompleted"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <!-- Player Info Section -->
    <div class="player-info-section">
      <div class="player-info">
        <Avatar
          :src="player.avatar_url"
          :alt="player.name"
          size="small"
          :show-frame="true"
          shape="hexagon"
          fallback-icon="bi-person"
        />
        <div class="player-details">
          <div class="player-name" :class="{ 'dummy-name': isDummyPlayer }">
            {{ isDummyPlayer ? t('tournament.dummy_player') : player.name }}
          </div>
          <div v-if="isDummyPlayer" class="dummy-label">{{ t('tournament.dummy_player_bye') }}</div>
        </div>
      </div>
      <div class="player-badges">
        <div v-if="isWinner" class="winner-badge">
          <i class="bi bi-trophy"></i>
        </div>
        <div v-if="isLoser" class="loser-badge">
          <i class="bi bi-x-circle"></i>
        </div>
        <div v-if="isTie" class="tie-badge">
          <i class="bi bi-dash-circle"></i>
        </div>
      </div>
    </div>

    <!-- Rosters Section -->
    <div v-if="rosters && rosters.length > 0 && !isDummyPlayer" class="rosters-section">
      <div v-if="hasMultipleRosters" class="rosters-label">
        {{ t('tournament.player_card.rosters') }}:
        <span
          v-if="isCompleted"
          class="rosters-disabled-note"
          :title="t('tournament.player_card.rosters_locked_title')"
        >
          {{ t('tournament.player_card.locked') }}
        </span>
        <span v-if="rosterUpdateSuccess" class="roster-update-success">
          <i class="bi bi-check-circle"></i> {{ t('tournament.player_card.updated') }}
        </span>
      </div>
      <div class="rosters-grid">
        <TournamentRosterCard
          v-for="(roster, index) in rosters"
          :key="roster.id"
          :roster="roster"
          :roster-index="index"
          :is-active="index === activeRosterIndex"
          :is-clickable="!isCompleted"
          @roster-click="handleRosterSelection"
          @roster-view="showRosterDetails"
          @roster-link="openRosterLink"
        />
      </div>
    </div>

    <div v-if="!isCompleted && !isDummyPlayer" class="scoring-inputs">
      <div class="input-group">
        <label class="input-label">{{ t('tournament.player_card.victory_points_label') }}:</label>
        <input
          :value="victoryPoints"
          @input="handleVictoryPointsChange"
          @keydown="handleKeyDown"
          @paste="handlePaste"
          @blur="handleBlur"
          @focus="handleFocus"
          type="number"
          min="0"
          step="1"
          class="scoring-input victory-points-input"
          :class="{ 'invalid': !victoryPointsValid }"
          placeholder="0"
          :title="t('tournament.player_card.positive_int_or_zero_title')"
        />
        <div v-if="!victoryPointsValid" class="validation-error">
          {{ t('tournament.player_card.positive_int_or_zero_error') }}
        </div>
      </div>
      <div class="input-group">
        <label class="input-label">{{ t('tournament.player_card.models_cost_label') }}:</label>
        <input
          :value="modelsCost"
          @input="handleModelsCostChange"
          @keydown="handleKeyDown"
          @paste="handlePaste"
          @blur="handleBlur"
          @focus="handleFocus"
          type="number"
          min="0"
          step="1"
          class="scoring-input models-cost-input"
          :class="{ 'invalid': !modelsCostValid }"
          placeholder="0"
          :title="t('tournament.player_card.positive_int_or_zero_title')"
        />
        <div v-if="!modelsCostValid" class="validation-error">
          {{ t('tournament.player_card.positive_int_or_zero_error') }}
        </div>
      </div>
    </div>

    <div v-else class="match-result">
      <div class="result-points">
        <span class="victory-points">{{ victoryPoints }} {{ t('structure.points') }}</span>
        <span class="models-cost">{{ modelsCost }} {{ t('tournament.player_card.models_cost_short') }}</span>
      </div>
    </div>
  </div>

  <!-- Roster Details Modal -->
  <RosterDetailsModal
    :roster="selectedRoster"
    :is-visible="showRosterModal"
    @close="closeRosterModal"
  />
</template>

<style lang="scss" scoped>
// Color Variables
$primary-bg: #efd8b0;
$primary-text: #bd9c65;
$icon-bg: #c29c58;
$icon-text: #60451f;
$hover-bg: #b08f4f;
$border-color: #bd9c65;
$focus-shadow: rgba(189, 156, 101, 0.2);
$winner-color: #4caf50;
$loser-color: #f44336;
$tie-color: #ff9800;
$drag-over-color: #2196f3;

.player-card {
  background-color: white;
  border: 2px solid $border-color;
  border-radius: 8px;
  padding: 12px;
  min-height: 120px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.2s ease;
  position: relative;
  box-sizing: border-box;

  &.draggable {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }

  &.dragging {
    opacity: 0.5;
    transform: scale(0.95);
  }

  &.drop-zone {
    border-style: dashed;
    border-color: $border-color;
    background-color: rgba($primary-bg, 0.3);

    &.drag-over {
      border-color: $drag-over-color;
      background-color: rgba($drag-over-color, 0.1);
      transform: scale(1.02);
    }
  }

  &.current-opponent {
    border-color: $loser-color;
    background-color: rgba($loser-color, 0.05);
    cursor: not-allowed;
  }

  &.winner {
    border-color: $winner-color;
    background-color: rgba($winner-color, 0.1);

    .player-name {
      color: $winner-color;
      font-weight: bold;
    }
  }

  &.loser {
    border-color: $loser-color;
    background-color: rgba($loser-color, 0.05);

    .player-name {
      color: $loser-color;
    }
  }

  &.tie {
    border-color: $tie-color;
    background-color: rgba($tie-color, 0.1);

    .player-name {
      color: $tie-color;
      font-weight: bold;
    }
  }

  &.completed {
    background-color: #f8f9fa;
    border-color: #dee2e6;
  }

  &.dummy-player {
    background-color: #f0f0f0;
    border-color: #999;
    border-style: dashed;
    opacity: 0.8;

    .player-name {
      color: #666;
      font-style: italic;
    }

    .dummy-label {
      color: #999;
      font-size: 10px;
      font-style: italic;
    }
  }

  &.compact {
    padding: 8px;
    min-height: 80px;



    .player-name {
      font-size: 12px;
    }

    .rosters-section {
      margin-top: 6px;

      .rosters-label {
        font-size: 9px;
        margin-bottom: 3px;
      }

      .rosters-grid {
        gap: 3px;
      }
    }

    .scoring-inputs {
      gap: 4px;
    }

    .input-label {
      font-size: 9px;
    }

    .scoring-input {
      padding: 2px 4px;
      font-size: 10px;
      width: 40px;
    }

    .match-result {
      gap: 2px;
    }

    .result-points {
      font-size: 10px;
    }
  }
}

.player-info-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  gap: 8px;
}

.player-info {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}



.player-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.player-name {
  font-weight: bold;
  color: $primary-text;
  font-size: 14px;
  line-height: 1.2;
  word-wrap: break-word;
  margin-bottom: 2px;
  text-align: left;
}



.player-badges {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.winner-badge, .loser-badge, .tie-badge {
  font-size: 16px;
}

.winner-badge {
  color: $winner-color;
}

.loser-badge {
  color: $loser-color;
}

.tie-badge {
  color: $tie-color;
}



  .rosters-section {
    margin-top: 8px;

    .rosters-label {
      font-size: 10px;
      color: $primary-text;
      font-weight: bold;
      margin-bottom: 4px;
    }

    .rosters-disabled-note {
      color: #999;
      font-weight: normal;
      font-style: italic;
      font-size: 9px;
    }

    .roster-update-success {
      color: $winner-color;
      font-weight: bold;
      font-size: 9px;
      animation: fadeInOut 1s ease-in-out;

      i {
        font-size: 8px;
        margin-right: 2px;
      }
    }

    .rosters-grid {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }

.scoring-inputs {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  align-items: flex-end;
}

.input-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  width: 100%;
}

.input-label {
  font-size: 10px;
  color: $primary-text;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  flex-shrink: 0;
}

.scoring-input {
  padding: 3px 5px;
  border: 1px solid $border-color;
  border-radius: 4px;
  font-size: 11px;
  background-color: white;
  color: $primary-text;
  width: 45px;
  text-align: center;

  &:focus {
    outline: none;
    border-color: $icon-bg;
    box-shadow: 0 0 0 2px $focus-shadow;
  }

  &::placeholder {
    color: $primary-text;
    opacity: 0.5;
  }

  &.invalid {
    border-color: $loser-color;
    background-color: rgba($loser-color, 0.05);
    box-shadow: 0 0 0 2px rgba($loser-color, 0.2);

    &:focus {
      border-color: $loser-color;
      box-shadow: 0 0 0 2px rgba($loser-color, 0.3);
    }
  }
}

.match-result {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.result-points {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
}

.victory-points {
  color: $winner-color;
  font-weight: bold;
}

.models-cost {
  color: $icon-bg;
  font-weight: 500;
}

.validation-error {
  font-size: 9px;
  color: $loser-color;
  margin-top: 2px;
  text-align: right;
  font-weight: 500;
  line-height: 1.2;
}

// Hover effects
.player-card:not(.current-opponent):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

// Responsive design
@media (max-width: 768px) {
  .player-card {
    padding: 10px;
    min-height: auto;
    gap: 6px;
  }

  .player-info-section {
    margin-bottom: 6px;
    gap: 6px;
  }

  .player-info {
    gap: 6px;
  }

  .player-name {
    font-size: 13px;
    margin-bottom: 1px;
  }

  .player-badges {
    gap: 2px;
  }

  .winner-badge, .loser-badge, .tie-badge {
    font-size: 14px;
  }

  .rosters-section {
    margin-top: 6px;

    .rosters-label {
      font-size: 9px;
      margin-bottom: 3px;
    }

    .rosters-grid {
      gap: 3px;
    }
  }

  .scoring-inputs {
    gap: 4px;
  }

  .input-group {
    gap: 6px;
  }

  .input-label {
    font-size: 9px;
  }

  .scoring-input {
    font-size: 11px;
    padding: 3px 5px;
    width: 40px;
  }

  .match-result {
    gap: 2px;
  }

  .result-points {
    font-size: 10px;
    gap: 1px;
  }

  .validation-error {
    font-size: 8px;
    margin-top: 1px;
  }

  .player-card {
    &.compact {
      padding: 6px;
      min-height: 60px;

      .player-name {
        font-size: 11px;
      }

      .rosters-section {
        margin-top: 4px;

        .rosters-label {
          font-size: 8px;
          margin-bottom: 2px;
        }

        .rosters-grid {
          gap: 2px;
        }
      }

      .scoring-inputs {
        gap: 3px;
      }

      .input-label {
        font-size: 8px;
      }

      .scoring-input {
        padding: 1px 3px;
        font-size: 9px;
        width: 35px;
      }

      .match-result {
        gap: 1px;
      }

      .result-points {
        font-size: 9px;
      }
    }
  }
}

@media (max-width: 480px) {
  .player-card {
    padding: 8px;

    &.compact {
      padding: 5px;
      min-height: 50px;

      .player-name {
        font-size: 10px;
      }

      .scoring-input {
        width: 30px;
        font-size: 8px;
      }

      .input-label {
        font-size: 7px;
      }
    }
  }

  .player-name {
    font-size: 12px;
  }

  .scoring-input {
    width: 35px;
    font-size: 10px;
  }

  .input-label {
    font-size: 8px;
  }
}

// Animations
@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
}
</style>
